// @flow
import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { CSVLink } from 'react-csv';
import debounce from 'lodash.debounce';
import {
  Card,
  Table,
  Row,
  Col,
  Tooltip,
  Badge,
  Icon,
} from 'antd';
import {
  SpButton,
  FilterWrapper,
  SpText,
} from 'components/DesignKit';

import Loading from 'components/Loading';
import Filters from 'components/Filters';
import getFilterOperators from 'utils/getFilterOperators';

import {
  notificationActions,
  adminBinActions,
  filterParamsActions,
} from 'store/actions';
import { FILTERS_SPECS } from 'store/actionTypes';
import {
  PAGES,
  ROLES,
  FILTERS_TYPES,
} from 'appconstants';

import queryString from 'query-string';
import { useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import RBAC from 'components/rbac';
import FilterIcon from 'components/NavIcons/filterIcon';
import ExportIcon from 'components/NavIcons/exportIcon';
import AddAdminBin from './components/AddAdminBin';
import EditAdminBin from './components/EditAdminBin';

const MOMENT_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
const BIN_FILTERS_TYPES = FILTERS_TYPES.ADMIN_BINS;

type Props = {
  adminBin: Array<Object>,
  fetchAdminBin: Function,
  addAdminBin: Function,
  selectedAccount: Object,
  totalElements: number,
  test: boolean,
  loading: boolean,
  submitting: boolean,
  validator: boolean,
  history: {
    push: Function,
  },
  updateAdminBin: Function,
  setFilterData: Function,
  preserveFilters: Object,
  isMobileView: Boolean,
};

const AdminBin = (props: Props) => {
  const location = useLocation();
  const {
    selectedAccount,
    adminBin,
    loading,
    addAdminBin,
    fetchAdminBin,
    totalElements,
    submitting,
    validator,
    test,
    history,
    updateAdminBin,
    setFilterData,
    preserveFilters,
    isMobileView,
  } = props;

  const tableId = document.getElementsByTagName('table');
  if (tableId && tableId[0] && isMobileView) {
    tableId[0].style.width = '1600px';
  } else if (tableId && tableId[0] && !isMobileView) {
    tableId[0].style.width = '100%';
  }

  const [pagination, setPagination] = useState({});
  const [addAdminBinModal, setAddAdminBinModal] = useState(false);
  const [editAdminBinModal, setEditAdminBinModal] = useState(false);
  const [selectedBin, setSelectedBin] = useState({});
  const myRef = useRef();
  const { pathname } = location;
  const [open, setOpen] = useState(false);
  const [filterTypes, setFilterTypes] = useState(BIN_FILTERS_TYPES);
  const [fiterOperators, setFilterOperators] = useState([]);
  const [filterBins, setFilterBins] = useState((preserveFilters && preserveFilters.filterBins)
    ? (preserveFilters && preserveFilters.filterBins)
    : [{
      filterType: '',
      filterName: '',
      filterOperator: '',
      filterValue: '',
      filterNameDisplay: '',
      filterValueFrom: '',
      filterValueTo: '',
      checked: true,
    }]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentDate, setCurrentDate] = useState('');
  const [count, setCount] = useState(null);
  const [fillColor, setFillColor] = useState(false);
  const [filterColor, setFilterColor] = useState(false);
  const preserveParam = ((preserveFilters && preserveFilters.pathname) === pathname)
    ? queryString.parse(preserveFilters.search)
    : setFilterData({
      pathname,
      search: '',
      filterBins: [{
        filterType: '',
        filterName: '',
        filterOperator: '',
        filterValue: '',
        filterNameDisplay: '',
        filterValueFrom: '',
        filterValueTo: '',
        checked: true,
      }],
    });

  const exportData = adminBin ? adminBin.map((values) => ({
    bin: values.bin,
    country: values.country.alpha2,
    currency: values.currency.name,
    scheme: values.scheme,
    type: values.type,
    bankName: values.bankName,
    updatedAt: moment
      .utc(values.updatedAt)
      .tz(selectedAccount.timezone)
      .format('MMM DD, YYYY, hh:mm a'),
  })) : '';

  const exportHeaders = [
    { label: 'BIN', key: 'bin' },
    { label: 'COUNTRY', key: 'country' },
    { label: 'CURRENCY', key: 'currency' },
    { label: 'SCHEME', key: 'scheme' },
    { label: 'TYPE', key: 'type' },
    { label: 'BANK', key: 'bankName' },
    { label: 'DATE', key: 'updatedAt' },
  ];

  const debouncedFetchAdminBin = debounce(fetchAdminBin, 2000, { leading: true });

  const getData = (query) => {
    const parsed = queryString.parse(query);
    const currentPage = parsed.pageNum || 1;
    const sortBy = parsed.sortBy || 'createdAt';
    const direction = parsed.direction || 'DESC';
    const sortParams = `&sortBy=${sortBy}&direction=${direction}`;
    const filterParam = parsed.filterParams || '';
    const filterParams = (preserveParam ? preserveParam.filterParams : filterParam || '');
    debouncedFetchAdminBin({ currentPage, sortParams, filterParams });
  };

  useEffect(() => {
    if (!validator) {
      setAddAdminBinModal(false);
      setEditAdminBinModal(false);
    }
  }, [validator]);

  const handleClick = e => {
    if (myRef.current && myRef.current.contains(e.target)) {
      return;
    }
    if (e.target.value === undefined
      && e.toElement.className !== 'ant-calendar-date'
      && e.toElement.className !== 'ant-calendar-prev-month-btn'
      && e.toElement.className !== 'ant-calendar-next-month-btn') {
      setOpen(false);
    }
  };

  useEffect(() => {
    getData(location.search);
  }, [location, test, selectedAccount]);

  useEffect(() => {
    const parsed = queryString.parse(location.search);
    const currentPage = parsed.pageNum || 1;
    setPagination(existingElements => ({
      ...existingElements,
      total: totalElements,
      current: parseInt(currentPage, 10),
      showTotal: total => `Total ${total} items`,
    }));
  }, [adminBin]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  const addNewAdminBin = (binInfo: Object) => {
    const {
      bin,
      brand,
      country,
      currency,
      type,
      scheme,
      bank,
    } = binInfo;
    addAdminBin({
      bin,
      brand,
      countryCode: country,
      currencyCode: currency,
      prepaid: false,
      scheme,
      type,
      bankName: bank,
    });
  };

  const handleTableChange = (page, sorter, filters) => {
    const parsed = queryString.parse(location.search);
    const filterParams = parsed.filterParams || '';
    const tempPage = page.current;
    let tempParams = '';
    if (filters.order === 'ascend') {
      tempParams = `&sortBy=${filters.field}&direction=ASC`;
    } else if (filters.order === 'descend') {
      tempParams = `&sortBy=${filters.field}&direction=DESC`;
    } else {
      tempParams = '';
    }
    if (location.search && filterParams) {
      const filterParam = encodeURIComponent(filterParams);
      history.push({
        pathname: PAGES.ADMIN_BIN,
        search: `?filterParams=${filterParam}&pageNum=${tempPage}${tempParams}`,
      });
    } else {
      history.push({
        pathname: PAGES.ADMIN_BIN,
        search: `?pageNum=${tempPage}${tempParams}`,
      });
    }
  };

  const onCheck = (e, index) => {
    const list = [...filterBins];
    if (filterBins.length !== 1) {
      if (!e) {
        list.splice(index, 1);
        const filterTypeOptions = BIN_FILTERS_TYPES.filter(({ value: id1 }) => !list.some(({ filterName: id2 }) => id2 === id1));
        setFilterTypes(filterTypeOptions);
      } else {
        list[index].checked = e;
      }
    }
    setFilterBins(list);
  };

  const handleChange = (e, index) => {
    const list = [...filterBins];
    if (e[1] === 'createdAt') {
      list[index].filterType = 'Date';
    } else if (e[1] === 'amount') {
      list[index].filterType = 'Number';
    } else if (e[1] === 'status') {
      list[index].filterType = 'Dropdown';
    } else {
      list[index].filterType = 'String';
    }
    list[index].filterName = e[1];
    list[index].filterNameDisplay = e[0];
    list[index].filterOperator = '';
    list[index].filterValue = '';
    const filterTypeOptions = BIN_FILTERS_TYPES.filter(({ value: id1 }) => !list.some(({ filterName: id2 }) => id2 === id1));
    setFilterBins(list);
    setFilterTypes(filterTypeOptions);
    setFilterOperators(getFilterOperators(e[1]));
  };

  const handleFromValue = (e, index) => {
    const list = [...filterBins];
    const regex = /^\d*\.?\d*$/;
    if (e.currentTarget && regex.test(e.currentTarget.value.replace(/,/g, ''))) {
      list[index].filterValueFrom = e.currentTarget.value;
      setFilterBins(list);
    } else if (regex.test(e.replace(/,/g, ''))) {
      list[index].filterValueFrom = e;
      setFilterBins(list);
    }
  };

  const handleToValue = (e, index) => {
    const list = [...filterBins];
    const regex = /^\d*\.?\d*$/;
    if (e.currentTarget && regex.test(e.currentTarget.value.replace(/,/g, ''))) {
      list[index].filterValueTo = e.currentTarget.value;
      setFilterBins(list);
    } else if (regex.test(e.replace(/,/g, ''))) {
      list[index].filterValueTo = e;
      setFilterBins(list);
    }
  };

  const handleValue = (e, index) => {
    let selectedValue = '';
    if (filterBins[index].filterName === 'createdAt') {
      if (filterBins[index].filterOperator === 'BT') {
        const date1 = e[0];
        const date2 = e[1];
        const utcDate1 = moment(date1).startOf('day');
        setStartDate(utcDate1);
        const utcDate2 = moment(date2).startOf('day');
        setEndDate(utcDate2);
        const utcString1 = utcDate1.format(MOMENT_FORMAT);
        const utcString2 = utcDate2.format(MOMENT_FORMAT);
        const selectedDate1 = moment.tz(utcString1, selectedAccount.timezone);
        const selectedDate2 = moment.tz(utcString2, selectedAccount.timezone);
        const dateStart = selectedDate1.utc().format(MOMENT_FORMAT);
        const dateEnd = selectedDate2.utc().format(MOMENT_FORMAT);
        selectedValue = `createdAt.GT=${dateStart}&createdAt.LT=${dateEnd}`;
      } else if (filterBins[index].filterOperator === 'LT') {
        const utcDate = moment(e).startOf('day');
        setCurrentDate(utcDate);
        const utcString = utcDate.format(MOMENT_FORMAT);
        const dateSelected = moment.tz(utcString, selectedAccount.timezone);
        const dateStart = dateSelected.utc().format(MOMENT_FORMAT);
        selectedValue = `createdAt.LT=${dateStart}`;
      } else if (filterBins[index].filterOperator === 'GT') {
        const utcDate = moment(e).startOf('day');
        setCurrentDate(utcDate);
        const utcString = utcDate.format(MOMENT_FORMAT);
        const dateSelected = moment.tz(utcString, selectedAccount.timezone);
        const dateStart = dateSelected.utc().format(MOMENT_FORMAT);
        selectedValue = `createdAt.GT=${dateStart}`;
      }
    } else if (filterBins[index].filterName === 'status') {
      selectedValue = e;
    } else {
      if (!e.currentTarget) {
        selectedValue = e;
      }
      if (e.currentTarget && e.currentTarget.value) {
        selectedValue = e.currentTarget.value;
      }
    }
    const list = [...filterBins];
    list[index].filterValue = selectedValue;
    setFilterBins(list);
  };

  const handleOperator = (e, index) => {
    const list = [...filterBins];
    list[index].filterOperator = e;
    if (list[index].filterValue) {
      if (list[index].filterOperator !== 'BT'
        && list[index].filterName === 'createdAt') {
        handleValue(currentDate, index);
      } else if (list[index].filterOperator === 'BT'
        && list[index].filterName === 'createdAt') {
        const dateList = [startDate, endDate];
        handleValue(dateList, index);
      } else if (list[index].filterOperator !== 'BT'
        && list[index].filterOperator !== 'LT'
        && list[index].filterOperator !== 'GT') {
        const value = filterBins[index].filterValue;
        handleValue(value, index);
      }
    }
    setFilterBins(list);
  };

  const handleAddClick = () => {
    const list = [...filterBins];
    setFilterBins([...list, {
      filterType: '',
      filterName: '',
      filterOperator: '',
      filterValue: '',
      filterNameDisplay: '',
      filterValueFrom: '',
      filterValueTo: '',
      checked: true,
    }]);
  };

  const resetFilters = () => {
    setFilterData({
      pathname,
      search: '',
    });
    setFilterBins([{
      filterType: '',
      filterName: '',
      filterOperator: '',
      filterValue: '',
      filterNameDisplay: '',
      filterValueFrom: '',
      filterValueTo: '',
      checked: true,
    }]);
    setFilterTypes(BIN_FILTERS_TYPES);
    setStartDate('');
    setEndDate('');
    setCurrentDate('');
    setCount(null);
    history.push(PAGES.ADMIN_BIN);
  };

  const applyFilters = () => {
    const binsFilters = filterBins.filter((item) => item.checked);
    for (let i = 0; i < binsFilters.length; i += 1) {
      if (!binsFilters[i].filterName || !binsFilters[i].filterOperator || !binsFilters[i].filterValue) {
        return;
      }
    }
    if (binsFilters[0].filterValue || (binsFilters[0].filterValueFrom && binsFilters[0].filterValueTo)) {
      setCount(binsFilters && binsFilters.length);
      for (let i = 0; i < binsFilters.length; i += 1) {
        if (binsFilters[i].checked) {
          if (binsFilters[i].filterName === 'createdAt') {
            binsFilters[i] = binsFilters[i].filterValue;
          } else if (binsFilters[i].filterName === 'amount' && binsFilters[i].filterOperator === 'BT') {
            const name = binsFilters[i].filterName;
            const from = binsFilters[i].filterValueFrom.replace(/[. ,:-]+/g, '');
            const to = binsFilters[i].filterValueTo.replace(/[. ,:-]+/g, '');
            binsFilters[i] = `${name}.GT=${from}&${name}.LT=${to}`;
          } else if (binsFilters[i].filterName === 'amount' && binsFilters[i].filterOperator !== 'BT') {
            const value = binsFilters[i].filterValue.replace(/[. ,:-]+/g, '');
            binsFilters[i] = `${binsFilters[i].filterName}.${binsFilters[i].filterOperator}=${value}`;
          } else {
            binsFilters[i] = `${binsFilters[i].filterName}.${binsFilters[i].filterOperator}=${binsFilters[i].filterValue}`;
          }
        }
      }

      const filterParams = encodeURIComponent(binsFilters.join('&'));
      const parsed = queryString.parse(location.search);
      const currentPage = 1;
      const sortBy = parsed.sortBy || 'createdAt';
      const direction = parsed.direction || 'DESC';
      const sortParams = `&sortBy=${sortBy}&direction=${direction}`;
      if (location.search && parsed.sortBy && parsed.pageNum && parsed.direction) {
        setFilterData({
          pathname,
          search: `?filterParams=${filterParams}&pageNum=${currentPage}${sortParams}`,
          filterBins,
        });
        history.push({
          pathname: PAGES.ADMIN_BIN,
          search: `?filterParams=${filterParams}&pageNum=${currentPage}${sortParams}`,
        });
      } else {
        setFilterData({
          pathname,
          search: `?filterParams=${filterParams}`,
          filterBins,
        });
        history.push({
          pathname: PAGES.ADMIN_BIN,
          search: `?filterParams=${filterParams}`,
        });
      }
      setOpen(false);
    }
  };

  const showEditBinModal = (value: Object) => {
    setEditAdminBinModal(true);
    setSelectedBin(value);
  };

  const editSelectedBin = (binObject: Object) => {
    const parsed = queryString.parse(location.search);
    const currentPage = parsed.pageNum || 1;
    const sortBy = parsed.sortBy || 'createdAt';
    const direction = parsed.direction || 'DESC';
    const sortParams = `&sortBy=${sortBy}&direction=${direction}`;
    const {
      bin,
      brand,
      country,
      currency,
      type,
      scheme,
      bank,
      id,
    } = binObject;

    const params = {
      bin,
      brand,
      countryCode: country,
      currencyCode: currency,
      prepaid: false,
      scheme,
      type,
      bankName: bank,
    };

    updateAdminBin({
      currentPage,
      sortParams,
      gid: id,
      params,
    });
  };


  const columns = [
    {
      title: 'BIN',
      dataIndex: 'bin',
      width: '12%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (bin) => (
        <span>
          {bin || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: 'COUNTRY',
      dataIndex: 'country',
      width: '12%',
      align: 'left',
      sorter: false,
      render: (text, record) => (
        <span style={{ cursor: 'pointer' }}>
          {record.country.alpha2 || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: 'CURRENCY',
      dataIndex: 'currency',
      width: '14%',
      align: 'left',
      render: (text, record) => (
        <span style={{ cursor: 'pointer' }}>
          {record.currency.name || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: 'SCHEME',
      dataIndex: 'scheme',
      width: '12%',
      align: 'left',
      sorter: false,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (scheme) => (
        <span>
          {scheme || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: 'TYPE',
      dataIndex: 'type',
      width: '12%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (type) => (
        <span>
          {type || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: 'BANK',
      dataIndex: 'bankName',
      width: '12%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (bankName) => (
        <span>
          {bankName || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: 'DATE',
      dataIndex: 'updatedAt',
      width: '14%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (updatedAt) => {
        if (updatedAt && selectedAccount && selectedAccount.timezone) {
          const tzDate = moment
            .utc(updatedAt)
            .tz(selectedAccount.timezone)
            .format('MMM DD, YYYY, hh:mm a');
          return (<span>{tzDate}</span>);
        }
        return (
          <>&#8211;</>
        );
      },
    },
  ];

  const actionColumn = {
    title: 'ACTION',
    dataIndex: 'action',
    width: '12%',
    align: 'center',
    render: (text, record) => (
      <span>
        <Tooltip placement="top" title="Edit">
          <Icon
            type="edit"
            style={{ fontSize: '20px', cursor: 'pointer' }}
            onClick={(e) => { e.stopPropagation(); showEditBinModal(record); }}
          />
        </Tooltip>
      </span>
    ),
  };

  if (loading || !selectedAccount) {
    return <Loading />;
  }

  return (
    <Card>
      <Row className="my-2">
        <div className="d-flex justify-content-end">
          <RBAC permit={[ROLES.OWNER, ROLES.ADMINISTRATOR, ROLES.DEVELOPER, ROLES.OPERATIONS, ROLES.SUPERUSER]}>
            <Col>
              <SpButton
                type="secondary"
                shape="round"
                ghost
                className="mr-3"
                onClick={() => setAddAdminBinModal(true)}
              >
                {'\u002B'}&nbsp;Add Bin
              </SpButton>
              <span>
                {
                  exportData !== null
                    ? (
                      <Tooltip placement="left" title="Click here to export Bins">
                        <CSVLink
                          target="_blank"
                          className="mr-3"
                          filename="Bins Report.csv"
                          data={exportData}
                          headers={exportHeaders}
                          onMouseEnter={() => setFillColor(true)}
                          onMouseLeave={() => setFillColor(false)}
                        >
                          <ExportIcon
                            fill={fillColor
                              ? '#279dfe'
                              : '#C0C0C0'}
                          />
                        </CSVLink>
                      </Tooltip>
                    ) : null
                }
              </span>
              <span ref={myRef}>
                <span
                  onClick={() => setOpen(!open)}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setFilterColor(true)}
                  onMouseLeave={() => setFilterColor(false)}
                >
                  {open && (
                    <Badge count={count} style={{ backgroundColor: '#0090ff' }}>
                      <FilterIcon
                        fill={filterColor
                          ? '#279dfe'
                          : '#C0C0C0'}
                      />
                    </Badge>
                  )}
                  {!open && (
                    <Tooltip placement="left" title="Click here to filter Bins">
                      <Badge count={count} style={{ backgroundColor: '#0090ff' }}>
                        <FilterIcon
                          fill={filterColor
                            ? '#279dfe'
                            : '#C0C0C0'}
                        />
                      </Badge>
                    </Tooltip>
                  )}
                </span>
                {open && (
                  <FilterWrapper
                    style={{ marginLeft: '-148px' }}
                  >
                    <Filters
                      filtersGrid={filterBins}
                      filterTypes={filterTypes}
                      handleChange={handleChange}
                      onCheck={onCheck}
                      fiterOperators={fiterOperators}
                      handleOperator={handleOperator}
                      handleValue={handleValue}
                      startDate={startDate}
                      endDate={endDate}
                      currentDate={currentDate}
                      handleFromValue={handleFromValue}
                      handleToValue={handleToValue}
                    />
                    <div className="px-4 mt-2">
                      {
                        filterBins.length < BIN_FILTERS_TYPES.length && (
                          <div className="mb-2 ml-3" style={{ cursor: 'pointer' }}>
                            <SpText
                              fontWeight="600"
                              color="#279dfe"
                              onClick={handleAddClick}
                            >
                              +&nbsp; Add another filter
                            </SpText>
                          </div>
                        )
                      }
                    </div>
                    <div className="d-flex mb-2 justify-content-end mr-3">
                      <SpButton
                        type="secondary"
                        shape="round"
                        className="mr-2"
                        ghost
                        onClick={resetFilters}
                      >
                        Clear
                      </SpButton>
                      <SpButton
                        type="primary"
                        shape="round"
                        onClick={applyFilters}
                      >
                        Save
                      </SpButton>
                    </div>
                  </FilterWrapper>
                )}
              </span>
            </Col>
          </RBAC>
        </div>
      </Row>

      {/* ADD ADMIN BIN MODAL */}
      {addAdminBinModal && (
        <AddAdminBin
          visible={addAdminBinModal}
          submitting={submitting}
          close={() => setAddAdminBinModal(false)}
          submit={addNewAdminBin}
        />
      )}

      {/* EDIT BIN MODAL */}
      {editAdminBinModal && (
        <EditAdminBin
          visible={editAdminBinModal}
          submitting={submitting}
          selectedBin={selectedBin}
          close={() => setEditAdminBinModal(false)}
          submit={editSelectedBin}
        />
      )}

      <RBAC
        fallback={(
          <Table
            columns={columns}
            rowKey={(record) => record.gid}
            dataSource={adminBin}
            pagination={pagination}
            onChange={handleTableChange}
          />
        )}
        permit={[ROLES.OWNER, ROLES.ADMINISTRATOR, ROLES.DEVELOPER, ROLES.OPERATIONS, ROLES.SUPERUSER]}
      >
        <Table
          columns={[...columns, actionColumn]}
          rowKey={(record) => record.gid}
          dataSource={adminBin}
          pagination={pagination}
          onChange={handleTableChange}
        />
      </RBAC>
    </Card>
  );
};

const mapStateToProps = (state) => ({
  adminBin: state.adminBin.content,
  totalElements: state.adminBin.totalElements,
  loading: state.loading.loading,
  validator: state.loading.validator,
  submitting: state.loading.submitting,
  test: state.account.test,
  selectedAccount: state.account.selectedAccount,
  isSuperUser: state.user.superUser,
  preserveFilters: state.filterParams.preserveFilters,
  isMobileView: state.mobileView.isMobileView,
});

const mapDispatchToProps = (dispatch) => ({
  fetchAdminBin: param => dispatch(adminBinActions.fetchAdminBin({
    payload: param,
  })),
  addAdminBin: param => dispatch(adminBinActions.addAdminBin({
    payload: param,
  })),
  updateAdminBin: param => dispatch(adminBinActions.updateAdminBin({
    payload: param,
  })),
  setNotification: ({ type, payload }) => dispatch(notificationActions.setNotification({
    type,
    payload,
  })),
  setFilterData: (value) => dispatch(filterParamsActions.setFilterData({
    type: FILTERS_SPECS.SET_FILTER_DATA,
    payload: value,
  })),
});

// $FlowFixMe
export default connect(mapStateToProps, mapDispatchToProps)(AdminBin);
